import QueryString from 'query-string';
import { toastr } from 'react-redux-toastr';
import { push } from 'react-router-redux';
import { isErrorResponse } from '../../utils/error-handlers';
import {
  DEFAULT_DELETE_CONFIG,
  DEFAULT_GET_CONFIG,
  DEFAULT_POST_CONFIG,
  DEFAULT_PUT_CONFIG,
} from '../../config/rest';
import {
  CLEAR_PRICING_PROFILE_DATE_RANGE,
  CLEAR_PRICING_PROFILE_DATE_RANGE_FILTER,
  CLEAR_PRICING_PROFILE_DATE_RANGES,
  FLAG_GETTING_PRICING_PROFILE_DATE_RANGES,
  FLAG_PRICING_PROFILE_DATE_RANGE_ACTIVITY,
  GET_PRICING_PROFILE_DATE_RANGE,
  GET_PRICING_PROFILE_DATE_RANGE_FILTER,
  GET_PRICING_PROFILE_DATE_RANGES,
} from '../types/sales/PricingProfileDateRange';
import { PRICING_PROFILE_DATE_RANGE_ENDPOINT } from '../../config/endpoints';
import { PRICING_PROFILE_DATE_RANGE_PATH } from '../../config/paths';

const flagGettingPricingProfileDateRanges = (flag) => (dispatch) =>
  dispatch({
    type: FLAG_GETTING_PRICING_PROFILE_DATE_RANGES,
    payload: flag,
  });

const getPricingProfileDateRanges =
  async (tableFilters = null) =>
  async (dispatch) => {
    try {
      dispatch(flagGettingPricingProfileDateRanges(true));
      const query = tableFilters;
      const url = `${PRICING_PROFILE_DATE_RANGE_ENDPOINT}?${QueryString.stringify(
        query,
      )}`;
      const response = await fetch(url, { ...DEFAULT_GET_CONFIG });
      await isErrorResponse(response, null, dispatch);
      const pricingProfiles = await response.json();
      dispatch({
        type: GET_PRICING_PROFILE_DATE_RANGES,
        payload: pricingProfiles,
      });
    } catch (error) {
      toastr.error('Error', error.message);
    } finally {
      dispatch(flagGettingPricingProfileDateRanges(false));
    }
  };

const clearPricingProfileDateRanges = () => (dispatch) =>
  dispatch({
    type: CLEAR_PRICING_PROFILE_DATE_RANGES,
  });

const clearPricingProfileDateRange = () => (dispatch) =>
  dispatch({
    type: CLEAR_PRICING_PROFILE_DATE_RANGE,
  });

const flagPricingProfileDateRangeActivity = (flag) => (dispatch) =>
  dispatch({
    type: FLAG_PRICING_PROFILE_DATE_RANGE_ACTIVITY,
    payload: flag,
  });

const postPricingProfileDateRange =
  async ({ year, pricingProfileDateRangeList }) =>
  async (dispatch) => {
    try {
      dispatch(flagPricingProfileDateRangeActivity(true));

      const payload = {
        year,
        pricingProfileDateRangeList,
      };

      const url = PRICING_PROFILE_DATE_RANGE_ENDPOINT;

      const response = await fetch(url, {
        ...DEFAULT_POST_CONFIG,
        body: JSON.stringify(payload),
      });

      await isErrorResponse(response, null, dispatch);

      dispatch(push(`${PRICING_PROFILE_DATE_RANGE_PATH}`));
    } catch (error) {
      toastr.error('Error', error.message);
    } finally {
      dispatch(flagPricingProfileDateRangeActivity(false));
    }
  };

const getPricingProfileDateRange =
  async ({ pricingProfileDateRangeId }) =>
  async (dispatch) => {
    try {
      dispatch(flagPricingProfileDateRangeActivity(true));

      const url = `${PRICING_PROFILE_DATE_RANGE_ENDPOINT}/${pricingProfileDateRangeId}`;

      const response = await fetch(url, { ...DEFAULT_GET_CONFIG });

      await isErrorResponse(response, null, dispatch);

      const pricingProfileDateRange = await response.json();

      dispatch({
        type: GET_PRICING_PROFILE_DATE_RANGE,
        payload: pricingProfileDateRange,
      });
    } catch (error) {
      toastr.error('Error', error.message);
    } finally {
      dispatch(flagPricingProfileDateRangeActivity(false));
    }
  };

const putPricingProfileDateRange =
  async (
    pricingProfileDateRangeId,
    { year, startDate, endDate, reason, corporate },
  ) =>
  async (dispatch) => {
    try {
      dispatch(flagPricingProfileDateRangeActivity(true));

      const payload = {
        id: pricingProfileDateRangeId,
        year,
        startDate,
        endDate,
        reason,
        corporate,
      };

      const url = `${PRICING_PROFILE_DATE_RANGE_ENDPOINT}/${pricingProfileDateRangeId}`;

      const response = await fetch(url, {
        ...DEFAULT_PUT_CONFIG,
        body: JSON.stringify(payload),
      });

      await isErrorResponse(response, null, dispatch);

      const pricingProfileDateRange = await response.json();

      dispatch(
        push(
          `${PRICING_PROFILE_DATE_RANGE_PATH}/${pricingProfileDateRange.id}`,
        ),
      );
    } catch (error) {
      toastr.error('Error', error.message);
    } finally {
      dispatch(flagPricingProfileDateRangeActivity(false));
    }
  };

const deletePricingProfileDateRange =
  async ({ pricingProfileDateRangeId }) =>
  async (dispatch) => {
    try {
      dispatch(flagPricingProfileDateRangeActivity(true));

      const url = `${PRICING_PROFILE_DATE_RANGE_ENDPOINT}/${pricingProfileDateRangeId}`;

      const response = await fetch(url, {
        ...DEFAULT_DELETE_CONFIG,
      });

      await isErrorResponse(response, null, dispatch);

      dispatch(push(PRICING_PROFILE_DATE_RANGE_PATH));
    } catch (error) {
      toastr.error('Error', error.message);
    } finally {
      dispatch(flagPricingProfileDateRangeActivity(false));
    }
  };

const getPricingProfileDateRangeFilter =
  ({ year, corporate }) =>
  async (dispatch) => {
    try {
      dispatch(flagPricingProfileDateRangeActivity(true));

      const url = `${PRICING_PROFILE_DATE_RANGE_ENDPOINT}/filter?year=${year}&corporate=${corporate}`;

      const response = await fetch(url, { ...DEFAULT_GET_CONFIG });
      await isErrorResponse(response, null, dispatch);

      const pricingProfiles = await response.json();

      dispatch({
        type: GET_PRICING_PROFILE_DATE_RANGE_FILTER,
        payload: pricingProfiles,
      });
    } catch (error) {
      toastr.error('Error', error.message);
    } finally {
      dispatch(flagPricingProfileDateRangeActivity(false));
    }
  };

const clearPricingProfileDateRangeFilter = () => (dispatch) =>
  dispatch({
    type: CLEAR_PRICING_PROFILE_DATE_RANGE_FILTER,
  });

export {
  clearPricingProfileDateRange,
  clearPricingProfileDateRanges,
  getPricingProfileDateRange,
  getPricingProfileDateRanges,
  postPricingProfileDateRange,
  putPricingProfileDateRange,
  deletePricingProfileDateRange,
  getPricingProfileDateRangeFilter,
  clearPricingProfileDateRangeFilter,
};
